"use client";

import { type Dispatch, type SetStateAction, useLayoutEffect, useState } from "react";

// cacheComponents restores the previous route, popover included. Close before paint when this
// route is shown again. usePathname() can't be called here: it blocks prerender outside Suspense.
export const useDismissOnNavigate = (): [boolean, Dispatch<SetStateAction<boolean>>] => {
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const close = () => setOpen(current => (current ? false : current));
    close();

    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const anchor = target.closest("a[href]");
      if (!anchor || anchor.getAttribute("target") === "_blank") {
        return;
      }
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) {
        return;
      }
      close();
    };

    document.addEventListener("click", onClick, true);
    globalThis.addEventListener("popstate", close);
    return () => {
      document.removeEventListener("click", onClick, true);
      globalThis.removeEventListener("popstate", close);
    };
  }, []);

  return [open, setOpen];
};
